import React, { createContext, useCallback, useContext, useState } from "react";
import { X, ChevronRight, ArrowRight } from "lucide-react";

/**
 * DemoExplainer — global "what this would do" overlay.
 *
 * Used to stub out destructive / data-heavy buttons in the demo
 * (Export, Download, Bulk Replacement, System Reset, Restore Backup,
 * etc.) without making them actually fire. Instead of a noop click,
 * the button opens a step-by-step explainer modal in the SAME visual
 * pattern the field workflows use — Step 1 → Step 2 → AUTO consequence.
 *
 * Usage:
 *   1. Wrap App in <DemoExplainerProvider>
 *   2. Anywhere: const { showExplainer } = useDemoExplainer();
 *   3. <button onClick={() => showExplainer({ feature: "Export" })}>Export</button>
 *
 * Or pass full content:
 *   showExplainer({
 *     title: "Export Daily Reports · CSV / PDF",
 *     eyebrow: "DEMO · WHAT THIS WOULD DO",
 *     steps: ["Pick format", "Pick date range", "Confirm", "Download starts"],
 *     consequence: "PDFs / CSV stream to your downloads · audit trail logged",
 *     consequenceColor: "#65DA76",
 *   });
 */

const FEATURE_PRESETS = {
  Export: {
    title: "Export · CSV / PDF",
    body:  "Pull the current view into a downloadable file.",
    steps: ["Pick format (CSV · PDF · XLSX)", "Pick date range / filters", "Confirm", "File streams to downloads"],
    consequence: "AUTO → file generated · audit trail logged · PM Folder mirror",
    consequenceColor: "#65DA76",
  },
  Download: {
    title: "Download · Bundle ZIP",
    body:  "Bundle the visible records + photos + signatures into a single archive.",
    steps: ["Confirm scope", "ZIP build", "Download begins"],
    consequence: "AUTO → zipped + checksum verified · stored in audit log",
    consequenceColor: "#65DA76",
  },
  "Bulk Replacement": {
    title: "Bulk Replacement",
    body:  "Replace a value across many records at once. Disabled in the public demo to protect seeded data.",
    steps: ["Pick target field", "Specify replacement", "Preview affected rows", "Confirm + execute"],
    consequence: "AUTO → diff stored · before/after audit log · rollback available 14d",
    consequenceColor: "#FFD000",
  },
  "System Reset": {
    title: "System Reset",
    body:  "Wipe demo data and restore a clean seed. Disabled in the public demo to keep tour state stable for visitors.",
    steps: ["Confirm scope (demo only · never production)", "Type RESET to confirm", "Background reset job runs", "Fresh seed in ~30s"],
    consequence: "AUTO → demo restored · activity feed regenerated · all visitor sessions cleared",
    consequenceColor: "#FF6A6A",
  },
  "Restore Backup": {
    title: "Restore From Backup",
    body:  "Roll the system back to any 14-day snapshot. Disabled in the public demo.",
    steps: ["Pick snapshot date", "Pick scope (full / per-collection)", "Confirm", "Background restore"],
    consequence: "AUTO → record snapshot mounted · prior state preserved · audit logged",
    consequenceColor: "#FFD000",
  },
  "Sync Now": {
    title: "Force Sync · External Systems",
    body:  "Push the current operational state out to external systems (accounting, payroll, fleet). Disabled in the public demo.",
    steps: ["Pick targets", "Confirm payload", "Sync runs in background"],
    consequence: "AUTO → external systems updated · webhook receipts stored",
    consequenceColor: "#65DA76",
  },
  Backup: {
    title: "Run Backup · Off-Site Snapshot",
    body:  "Build a full off-site .zip of records, photos, signatures, and PDFs. Disabled in the public demo so the seeded tour state stays stable.",
    steps: ["Confirm scope", "Backup builds in background (5–30 sec)", "Snapshot stored on disk", "14-day retention enforced"],
    consequence: "AUTO → snapshot mounted · audit trail stored · downloadable to NAS",
    consequenceColor: "#65DA76",
  },
  Delete: {
    title: "Delete Record",
    body:  "Permanently remove this record from the live system. Disabled in the public demo so other visitors keep seeing realistic operational data.",
    steps: ["Pick record", "Confirm permanent delete", "Background delete + audit log entry", "Soft-deleted for 30d before purge"],
    consequence: "AUTO → audit log entry · related signatures preserved · rollback within 30d",
    consequenceColor: "#FF6A6A",
  },
  "Send Email": {
    title: "Email Record · Recipients",
    body:  "Send this record (PDF + summary + photos) to an external recipient list. Disabled in the public demo so seeded contact data isn't actually emailed.",
    steps: ["Pick recipients", "Add note + subject", "Send", "Recipients receive PDF + summary"],
    consequence: "AUTO → PDF generated · email logged · delivery receipts stored",
    consequenceColor: "#65DA76",
  },
  Import: {
    title: "Import · Bulk Upload",
    body:  "Upload a spreadsheet or PDF to bulk-import records into the system. Disabled in the public demo to protect seeded data.",
    steps: ["Pick file", "Preview parsed rows", "Confirm + import", "Background import + audit"],
    consequence: "AUTO → records created · audit log entry · rollback available 14d",
    consequenceColor: "#FFD000",
  },
};

const DemoExplainerCtx = createContext({ showExplainer: () => {} });

export function useDemoExplainer() {
  return useContext(DemoExplainerCtx);
}

export function DemoExplainerProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const showExplainer = useCallback((opts) => {
    if (!opts) return;
    let payload = opts;
    if (opts.feature && FEATURE_PRESETS[opts.feature]) {
      payload = { ...FEATURE_PRESETS[opts.feature], ...opts };
    }
    setData(payload);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    // delay clear so close animation isn't jarring
    setTimeout(() => setData(null), 200);
  }, []);

  return (
    <DemoExplainerCtx.Provider value={{ showExplainer }}>
      {children}
      {open && data && <Modal data={data} onClose={close} />}
    </DemoExplainerCtx.Provider>
  );
}

function Modal({ data, onClose }) {
  const eyebrow     = data.eyebrow || "DEMO · WHAT THIS WOULD DO";
  const title       = data.title;
  const body        = data.body;
  const steps       = data.steps || [];
  const consequence = data.consequence;
  const consequenceColor = data.consequenceColor || "#65DA76";

  // Session-memory acknowledgment — only surfaces for destructive presets
  // (Reset / Restore / Delete / Bulk Replacement) where the user benefits
  // from seeing what *would* be wiped. Read from the global the
  // OperationsLedger publishes on every submission.
  const showsSessionMemory = /^(System Reset|Restore From Backup|Restore Backup|Bulk Replacement|Delete|Import)/i.test(
    title || ""
  );
  const sessionMemory =
    showsSessionMemory && typeof window !== "undefined"
      ? window.__forgeopsLedgerStats || null
      : null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="demo-explainer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-explainer-title"
    >
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: "rgba(0,0,0,0.72)" }}
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full sm:max-w-xl rounded-t-xl sm:rounded-xl border-2 overflow-hidden"
        style={{ background: "#0B0B0B", borderColor: "#1F1F1F", boxShadow: "0 0 60px rgba(255,106,0,0.18)" }}
      >
        {/* Header */}
        <div className="px-5 sm:px-6 py-3 flex items-start justify-between gap-3 border-b" style={{ background: "#070707", borderColor: "#1A1A1A" }}>
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
              {eyebrow}
            </div>
            <h3 id="demo-explainer-title" className="font-display text-lg sm:text-xl font-black tracking-tight text-white leading-tight">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="demo-explainer-close"
            aria-label="Close"
            className="inline-flex items-center justify-center w-9 h-9 rounded shrink-0"
            style={{ background: "transparent", color: "#BDBDBD", border: "1px solid #2A2A2A" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5 sm:py-6">
          {body && (
            <p className="text-sm leading-relaxed mb-4" style={{ color: "#BDBDBD" }}>
              {body}
            </p>
          )}

          {sessionMemory && sessionMemory.total > 0 && (
            <div
              className="mb-4 rounded p-3"
              style={{
                background: "rgba(255,106,0,0.06)",
                border: "1px solid rgba(255,106,0,0.35)",
              }}
              data-testid="demo-explainer-session-memory"
            >
              <div
                className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold mb-1.5"
                style={{ color: "#FF6A00" }}
              >
                Session memory
              </div>
              <p className="text-[12px] leading-snug" style={{ color: "#E0E0E0" }}>
                You routed{" "}
                <span className="font-bold text-white">
                  {sessionMemory.total} submission{sessionMemory.total > 1 ? "s" : ""}
                </span>
                {" "}this session{sessionMemory.readable ? ` — ${sessionMemory.readable}` : ""}.
                In live mode these would remain archived to operator and project records before the demo workspace clears.
              </p>
            </div>
          )}

          {steps.length > 0 && (
            <div className="space-y-1.5 mb-4">
              {steps.map((s, i) => (
                <div
                  key={s}
                  className="flex items-center gap-3 px-3 py-2 rounded-md border"
                  style={{ background: "#0F0F0F", borderColor: "#1F1F1F" }}
                >
                  <span
                    className="font-mono text-[10px] font-bold w-6 h-6 inline-flex items-center justify-center rounded shrink-0"
                    style={{ background: "rgba(255,106,0,0.14)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.4)" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-white leading-tight">{s}</span>
                  {i < steps.length - 1 && (
                    <ChevronRight className="w-3 h-3 ml-auto shrink-0" style={{ color: "#3A3A3A" }} />
                  )}
                </div>
              ))}
            </div>
          )}

          {consequence && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-md mb-1"
              style={{
                background: `${consequenceColor}10`,
                border: `1px solid ${consequenceColor}55`,
              }}
            >
              <ArrowRight className="w-4 h-4 shrink-0 mt-0.5" style={{ color: consequenceColor }} />
              <span className="text-[12px] leading-snug text-white">
                {consequence}
              </span>
            </div>
          )}

          <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: "#8A8A8A" }}>
            this action is disabled in the public demo so the tour stays clean.
            in your deployment it runs as shown above.
          </p>
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 flex items-center justify-end gap-2 border-t" style={{ background: "#070707", borderColor: "#1A1A1A" }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="demo-explainer-ok"
            className="h-10 px-4 rounded font-mono text-[11px] uppercase tracking-[0.25em] font-bold"
            style={{ background: "#FF6A00", color: "#000", border: "1px solid #FF6A00" }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default DemoExplainerProvider;
